import { Handle, NodeProps, Position } from 'reactflow';
import { GitBranch, Zap } from 'lucide-react';
import { PipelineNodeData } from '../../types/pipeline';

export interface MultiSplitNodeData extends PipelineNodeData {
  config?: {
    splitType?: 'multi';
    rules?: Array<{
      name: string;
      condition: string;
      target: string;
      color?: string;
    }>;
  };
}

export default function MultiSplitNode({ data, selected }: NodeProps<MultiSplitNodeData>) {
  const rules = data.config?.rules || [];

  // Default colors for routes
  const defaultColors = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6'];

  // Route colors from config or defaults
  const routeColors = rules.map((rule, i) => rule.color || defaultColors[i % defaultColors.length]);

  return (
    <div
      className={`rounded-lg border bg-white px-4 py-3 shadow-sm min-w-[200px] ${
        selected ? 'border-orange-500 ring-2 ring-orange-200' : 'border-orange-200'
      }`}
    >
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="!bg-orange-500 !w-3 !h-3 !border-2 !border-orange-600"
      />

      {/* Node Header */}
      <div className="flex items-center gap-2 text-sm font-semibold text-orange-700 mb-3">
        <GitBranch className="h-4 w-4" />
        <span>{data.label}</span>
        <Zap className="h-3 w-3 text-orange-500 ml-auto" />
      </div>

      {data.description && (
        <p className="text-xs text-slate-500 mb-3">{data.description}</p>
      )}

      {/* Route Labels */}
      {rules.length > 0 && (
        <div className="space-y-1">
          {rules.map((rule, index) => (
            <div
              key={index}
              className="flex items-center gap-2 text-xs bg-slate-50 rounded px-2 py-1 border border-slate-200"
            >
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: routeColors[index] }}
              />
              <span className="font-medium text-slate-700">{rule.name}</span>
              <span className="text-slate-400">→</span>
              <span className="text-slate-500 truncate max-w-[80px]">{rule.target}</span>
            </div>
          ))}
        </div>
      )}

      {/* Output handles - positioned on the right */}
      {rules.map((rule, index) => {
        // Calculate vertical positions for handles
        const totalHeight = 40 + (rules.length * 24);
        const topSpacing = 16;
        const spacing = 24;
        const yPos = topSpacing + (index * spacing);

        // Calculate percentage position
        const topPercent = (yPos / totalHeight) * 100;

        return (
          <Handle
            key={index}
            type="source"
            position={Position.Right}
            id={rule.name}
            style={{
              top: `${topPercent}%`,
              backgroundColor: routeColors[index],
              border: '2px solid white',
            }}
            className="!w-3 !h-3"
          />
        );
      })}
    </div>
  );
}
